import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../theme";
import ChartCard from "./ChartCard";
import DimensionTable from "./DimensionTable";
import { ChartRow, formatCount, formatPct } from "./types";

interface DonutCardProps {
	title: string;
	note?: string;
	rows: ChartRow[];
	dimensionLabel: string;
	centerTop: string;
	centerBottom: string;
}

const arcPath = (
	cx: number,
	cy: number,
	r0: number,
	r1: number,
	a0: number,
	a1: number
): string => {
	const pt = (r: number, a: number) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
	const large = a1 - a0 > Math.PI ? 1 : 0;
	const p0 = pt(r1, a0);
	const p1 = pt(r1, a1);
	const p2 = pt(r0, a1);
	const p3 = pt(r0, a0);
	return (
		`M${p0[0].toFixed(2)} ${p0[1].toFixed(2)}` +
		`A${r1} ${r1} 0 ${large} 1 ${p1[0].toFixed(2)} ${p1[1].toFixed(2)}` +
		`L${p2[0].toFixed(2)} ${p2[1].toFixed(2)}` +
		`A${r0} ${r0} 0 ${large} 0 ${p3[0].toFixed(2)} ${p3[1].toFixed(2)}Z`
	);
};

const CX = 95;
const CY = 95;
const R0 = 56;
const R1 = 86;

const DonutCard: React.FC<DonutCardProps> = ({
	title,
	note,
	rows,
	dimensionLabel,
	centerTop,
	centerBottom
}) => {
	const { t, i18n } = useTranslation();

	const renderChart = () => {
		const total = rows.reduce((sum, row) => sum + row.pct, 0);
		if (total <= 0) {
			return (
				<Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
					{t("communityDashboard.empty")}
				</Typography>
			);
		}

		const segments: { row: ChartRow; d: string; stroke: boolean }[] = [];
		let angle = -Math.PI / 2;
		rows.forEach(row => {
			if (row.pct <= 0) return;
			const fraction = row.pct / total;
			if (fraction > 0.9995) {
				// A full 2π arc has coincident endpoints and SVG drops it — draw two halves.
				segments.push({ row, d: arcPath(CX, CY, R0, R1, angle, angle + Math.PI), stroke: false });
				segments.push({
					row,
					d: arcPath(CX, CY, R0, R1, angle + Math.PI, angle + 2 * Math.PI),
					stroke: false
				});
				angle += 2 * Math.PI;
				return;
			}
			const nextAngle = angle + fraction * Math.PI * 2;
			segments.push({ row, d: arcPath(CX, CY, R0, R1, angle, nextAngle), stroke: true });
			angle = nextAngle;
		});

		return (
			<Box
				display="flex"
				alignItems="center"
				justifyContent="center"
				gap={3}
				flexWrap="wrap"
				py={1}
			>
				<svg width="190" height="190" viewBox="0 0 190 190">
					{segments.map((segment, index) => (
						<Tooltip
							key={`${segment.row.id}-${index}`}
							title={`${formatCount(segment.row.count, i18n.language)} · ${formatPct(segment.row.pct, i18n.language)}`}
							placement="top"
							arrow
						>
							<path
								d={segment.d}
								fill={segment.row.color}
								stroke={segment.stroke ? zoullooColors.text.white : undefined}
								strokeWidth={segment.stroke ? 2 : undefined}
							/>
						</Tooltip>
					))}
					<text
						x={CX}
						y={CY - 1}
						textAnchor="middle"
						fontSize="26"
						fontWeight="800"
						fill={zoullooColors.secondary.dark}
					>
						{centerTop}
					</text>
					<text
						x={CX}
						y={CY + 17}
						textAnchor="middle"
						fontSize="11"
						fontWeight="700"
						fill={zoullooColors.text.secondary}
					>
						{centerBottom}
					</text>
				</svg>
				<Box display="flex" flexDirection="column" gap={1} minWidth={190}>
					{rows.map(row => (
						<Box
							key={row.id}
							display="grid"
							gridTemplateColumns="12px 1fr auto"
							alignItems="center"
							gap={1}
						>
							<Box
								sx={{
									width: 11,
									height: 11,
									borderRadius: "3px",
									backgroundColor: row.color
								}}
							/>
							<Typography variant="body2" color="text.secondary">
								{row.label}
							</Typography>
							<Typography variant="body2" fontWeight={700}>
								{formatCount(row.count, i18n.language)}{" "}
								<Typography
									component="span"
									variant="body2"
									color="text.secondary"
								>
									· {formatPct(row.pct, i18n.language)}
								</Typography>
							</Typography>
						</Box>
					))}
				</Box>
			</Box>
		);
	};

	return (
		<ChartCard
			title={title}
			note={note}
			renderChart={renderChart}
			renderTable={() => <DimensionTable rows={rows} dimensionLabel={dimensionLabel} />}
		/>
	);
};

export default DonutCard;
